/**
 * src/lib/data/hubspotOwners.ts — HubSpot owners directory (Phase 6.1b).
 *
 * Pairs with worker /api/hubspot/owners. Used by AdminUsers to render
 * the HubSpot-owner field as a dropdown instead of a free-form numeric
 * input. Open per §1.4 — view password is enough.
 *
 * Sibling of markets.ts: both surface HubSpot reference data behind a
 * worker cache (24h TTL on the worker side, no client-side caching).
 */

export interface HubspotOwner {
  /** Numeric ID, stringified. Stored verbatim on User.hubspotOwnerId. */
  id: string;
  /** "First Last" — falls back to email or id when names are blank. */
  name: string;
  /** May be null (rare but real for service accounts / disabled seats). */
  email: string | null;
}

export interface HubspotOwnersOptions {
  workerUrl: string;
  viewPassword: string;
  /** Force the worker to re-fetch from HubSpot (skips its 24h KV cache). */
  refresh?: boolean;
  signal?: AbortSignal;
}

export class HubspotOwnersError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "HubspotOwnersError";
    this.status = status;
    this.body = body;
  }
}

export async function listHubspotOwners(opts: HubspotOwnersOptions): Promise<HubspotOwner[]> {
  const qs = opts.refresh ? "?refresh=1" : "";
  const resp = await fetch(`${opts.workerUrl}/api/hubspot/owners${qs}`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new HubspotOwnersError(
      `listHubspotOwners failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return Array.isArray(body) ? body : [];
}
